import { useState, useEffect } from 'react';
import { areasApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { Plus, Settings, Trash2, Loader2, Save, X, Mail, UserPlus, Building2 } from 'lucide-react';
import api from '../lib/api';

const EMPRESA_FIELDS = [
  { key: 'razon_social', label: 'Razón Social', placeholder: 'Ej: MI EMPRESA SPA' },
  { key: 'rut', label: 'RUT', placeholder: 'Ej: 76.738.421-1' },
  { key: 'direccion', label: 'Dirección', placeholder: 'Ej: Av. Principal 123' },
  { key: 'giro', label: 'Giro', placeholder: 'Ej: INGENIERIA Y CONSTRUCCION' },
  { key: 'slogan', label: 'Slogan / Descripción', placeholder: 'Ej: Ingenieria - Fabricacion - Montaje' },
  { key: 'telefono', label: 'Teléfono', placeholder: 'Ej: +56 9 1234 5678' },
  { key: 'ciudad', label: 'Ciudad', placeholder: 'Ej: Puerto Montt' },
  { key: 'email', label: 'Email empresa', placeholder: 'Ej: contacto@miempresa.cl' },
  { key: 'jefe_adquisiciones', label: 'Jefe de Adquisiciones (fallback PDF)', placeholder: 'Ej: Carlos Chirino', helper: 'Aparece como firma "V.B. ADQUISICIONES" en PDFs de OC sin creador asignado.' },
];

export const Configuracion = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ nombre: '', codigo: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Email recipients config
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailSaving, setEmailSaving] = useState(false);

  // Company config
  const [empresa, setEmpresa] = useState({});
  const [empresaLoading, setEmpresaLoading] = useState(true);
  const [empresaSaving, setEmpresaSaving] = useState(false);

  // Proforma email config
  const [proformaEmail, setProformaEmail] = useState({ to: '', cc: '', auto_send_on_approve: true });
  const [proformaEmailLoading, setProformaEmailLoading] = useState(true);
  const [proformaEmailSaving, setProformaEmailSaving] = useState(false);

  // Parte Diario config
  const [emailAlmuerzo, setEmailAlmuerzo] = useState('');
  const [emailAlmuerzoSaving, setEmailAlmuerzoSaving] = useState(false);
  const [horario, setHorario] = useState({
    horario_lv_entrada: '08:00', horario_lv_salida_am: '13:00',
    horario_lv_entrada_pm: '14:00', horario_lv_salida: '18:00',
    horario_vi_salida: '17:00',
  });

  useEffect(() => {
    loadAreas();
    loadEmailConfig();
    loadEmpresa();
    loadParteConfig();
    loadProformaEmail();
  }, []);

  const loadProformaEmail = async () => {
    try {
      const res = await api.get('/config/proforma-email');
      setProformaEmail({
        to: (res.data?.to || []).join(', '),
        cc: (res.data?.cc || []).join(', '),
        auto_send_on_approve: res.data?.auto_send_on_approve !== false,
      });
    } catch { /* first time */ }
    setProformaEmailLoading(false);
  };

  const saveProformaEmail = async () => {
    setProformaEmailSaving(true);
    try {
      await api.put('/config/proforma-email', {
        to: proformaEmail.to,
        cc: proformaEmail.cc,
        auto_send_on_approve: proformaEmail.auto_send_on_approve,
      });
      toast({ title: 'Configuración de correo proforma guardada' });
    } catch (err) {
      toast({ title: 'Error al guardar', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
    setProformaEmailSaving(false);
  };

  const loadEmpresa = async () => {
    try {
      const res = await api.get('/config/empresa');
      setEmpresa(res.data || {});
    } catch { /* first time - empty */ }
    setEmpresaLoading(false);
  };

  const saveEmpresa = async () => {
    setEmpresaSaving(true);
    try {
      await api.put('/config/empresa', empresa);
      toast({ title: 'Datos de empresa guardados' });
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
    setEmpresaSaving(false);
  };

  const loadParteConfig = async () => {
    try {
      const res = await api.get('/rrhh/config/parte-diario');
      setEmailAlmuerzo(res.data?.email_destino_almuerzo || '');
      setHorario({
        horario_lv_entrada: res.data?.horario_lv_entrada || '08:00',
        horario_lv_salida_am: res.data?.horario_lv_salida_am || '13:00',
        horario_lv_entrada_pm: res.data?.horario_lv_entrada_pm || '14:00',
        horario_lv_salida: res.data?.horario_lv_salida || '18:00',
        horario_vi_salida: res.data?.horario_vi_salida || '17:00',
      });
    } catch { /* first time */ }
  };

  const saveParteConfig = async () => {
    setEmailAlmuerzoSaving(true);
    try {
      await api.put('/rrhh/config/parte-diario', {
        email_destino_almuerzo: emailAlmuerzo,
        ...horario,
      });
      toast({ title: 'Configuración RRHH guardada' });
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
    setEmailAlmuerzoSaving(false);
  };

  const loadAreas = async () => {
    try {
      const res = await areasApi.getAll();
      setAreas(res.data);
    } catch (error) {

      toast({ title: 'Error al cargar las áreas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadEmailConfig = async () => {
    try {
      const res = await api.get('/contabilidad/email-config');
      setEmailRecipients(res.data?.recipients || []);
    } catch { /* silent */ }
    setEmailLoading(false);
  };

  const saveEmailConfig = async (recipients) => {
    setEmailSaving(true);
    try {
      await api.put('/contabilidad/email-config', { recipients });
      setEmailRecipients(recipients);
      toast({ title: 'Destinatarios actualizados' });
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
    setEmailSaving(false);
  };

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast({ title: 'Ingrese un email válido', variant: 'destructive' });
      return;
    }
    if (emailRecipients.includes(email)) {
      toast({ title: 'Este email ya está en la lista', variant: 'destructive' });
      return;
    }
    const updated = [...emailRecipients, email];
    saveEmailConfig(updated);
    setNewEmail('');
  };

  const removeEmail = (email) => {
    const updated = emailRecipients.filter(e => e !== email);
    saveEmailConfig(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre || !formData.codigo) {
      toast({ title: 'Complete todos los campos', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await areasApi.create(formData);
      toast({ title: 'Área creada' });
      setShowForm(false);
      setFormData({ nombre: '', codigo: '' });
      loadAreas();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error al crear el área', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await areasApi.delete(id);
      toast({ title: 'Área eliminada' });
      loadAreas();
    } catch (_error) {
      toast({ title: 'Error al eliminar el área', variant: 'destructive' });
    } finally {
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (<div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-96" /></div>);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="configuracion-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Configuración</h1>
          <p className="text-muted-foreground">Administre la configuración del sistema</p>
        </div>
      </div>

      {/* Company Data Configuration */}
      <Card data-testid="empresa-config-card">
        <CardHeader>
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Building2 className="w-5 h-5" />Datos de la Empresa</CardTitle>
            <CardDescription>Estos datos aparecerán en las Órdenes de Compra (PDF y visor), documentos y reportes</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {empresaLoading ? <Skeleton className="h-40" /> : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {EMPRESA_FIELDS.map(f => (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={`emp-${f.key}`} className="text-xs font-semibold text-gray-600">{f.label}</Label>
                    <Input
                      id={`emp-${f.key}`}
                      data-testid={`empresa-${f.key}`}
                      value={empresa[f.key] || ''}
                      onChange={e => setEmpresa(prev => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      className="text-sm"
                    />
                    {f.helper && <p className="text-[10px] text-muted-foreground">{f.helper}</p>}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Los cambios se reflejarán en todos los PDFs generados y en el visor de OC.
                </p>
                <Button onClick={saveEmpresa} disabled={empresaSaving} data-testid="save-empresa-btn">
                  {empresaSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Datos
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Proforma Email Configuration */}
      <Card data-testid="proforma-email-config-card">
        <CardHeader>
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Mail className="w-5 h-5" />Correo de Proformas Aprobadas</CardTitle>
            <CardDescription>
              Destinatarios automáticos al aprobar una proforma. Se enviará el PDF adjunto al equipo de facturación.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {proformaEmailLoading ? <Skeleton className="h-40" /> : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="proforma-to" className="text-xs font-semibold text-gray-600">
                  Destinatario(s) Principal — TO <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="proforma-to"
                  data-testid="proforma-email-to"
                  value={proformaEmail.to}
                  onChange={e => setProformaEmail(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="rmartinez@chirinosteel.com"
                  className="text-sm"
                />
                <p className="text-[10px] text-muted-foreground">Separe varios correos con coma. Ej: <code>juan@empresa.cl, maria@empresa.cl</code></p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="proforma-cc" className="text-xs font-semibold text-gray-600">Con Copia (CC)</Label>
                <Input
                  id="proforma-cc"
                  data-testid="proforma-email-cc"
                  value={proformaEmail.cc}
                  onChange={e => setProformaEmail(prev => ({ ...prev, cc: e.target.value }))}
                  placeholder="c.chirino@chirinosteel.com, rchirino@chirinosteel.com"
                  className="text-sm"
                />
                <p className="text-[10px] text-muted-foreground">Opcional. Separe con coma.</p>
              </div>
              <div className="flex items-center gap-2 p-3 rounded bg-blue-50 border border-blue-200">
                <input
                  type="checkbox"
                  id="proforma-auto-send"
                  data-testid="proforma-auto-send"
                  checked={proformaEmail.auto_send_on_approve}
                  onChange={e => setProformaEmail(prev => ({ ...prev, auto_send_on_approve: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Label htmlFor="proforma-auto-send" className="text-sm cursor-pointer">
                  <b>Enviar correo automáticamente</b> cuando se apruebe una proforma
                </Label>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Origen: <code>c.chirino@chirinosteel.com</code> (Office 365 / Microsoft Graph).
                </p>
                <Button onClick={saveProformaEmail} disabled={proformaEmailSaving} data-testid="save-proforma-email-btn">
                  {proformaEmailSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Settings className="w-5 h-5" />Áreas de Trabajo</CardTitle>
            <CardDescription>Configure las áreas disponibles para las órdenes de trabajo</CardDescription>
          </div>
          <Button onClick={() => setShowForm(true)} data-testid="new-area-btn"><Plus className="w-4 h-4 mr-2" />Nueva Área</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {showForm && (
            <Card className="border-2 border-primary/20">
              <CardContent className="p-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">Nueva Área de Trabajo</h3>
                    <Button variant="ghost" size="icon" type="button" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nombre">Nombre *</Label>
                      <Input id="nombre" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Nombre del área" data-testid="input-area-nombre" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="codigo">Código *</Label>
                      <Input id="codigo" value={formData.codigo} onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })} placeholder="CODIGO" data-testid="input-area-codigo" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                    <Button type="submit" disabled={saving} data-testid="save-area-btn">
                      {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<Save className="w-4 h-4 mr-2" />Crear Área
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {deleteConfirm && (
            <Card className="border-2 border-destructive/50 bg-destructive/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div><p className="font-medium">¿Eliminar área "{deleteConfirm.nombre}"?</p><p className="text-sm text-muted-foreground">Esta acción desactivará el área.</p></div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(deleteConfirm.id)}>Eliminar</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {areas.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground"><Settings className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No hay áreas configuradas</p></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow><TableHead>Nombre</TableHead><TableHead>Código</TableHead><TableHead>Estado</TableHead><TableHead className="w-12"></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {areas.map((area) => (
                  <TableRow key={area.id} data-testid={`area-row-${area.codigo}`}>
                    <TableCell className="font-medium">{area.nombre}</TableCell>
                    <TableCell className="font-mono">{area.codigo}</TableCell>
                    <TableCell><Badge variant={area.activa ? 'default' : 'secondary'}>{area.activa ? 'Activa' : 'Inactiva'}</Badge></TableCell>
                    <TableCell><Button variant="ghost" size="icon" onClick={() => setDeleteConfirm(area)} data-testid={`delete-area-${area.codigo}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Email Recipients Configuration */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Mail className="w-5 h-5" />Destinatarios de Correo</CardTitle>
            <CardDescription>Configure los destinatarios para alertas, rechazos SII y notificaciones del sistema</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {emailLoading ? (
            <Skeleton className="h-20" />
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {emailRecipients.map((email) => (
                  <div key={email} className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5" data-testid={`email-chip-${email.split('@')[0]}`}>
                    <Mail className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-sm text-blue-800">{email}</span>
                    <button onClick={() => removeEmail(email)} className="ml-1 text-blue-400 hover:text-red-500 transition-colors" data-testid={`remove-email-${email.split('@')[0]}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {emailRecipients.length === 0 && (
                  <p className="text-sm text-muted-foreground">No hay destinatarios configurados</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addEmail()}
                  placeholder="nuevo@email.com"
                  className="max-w-xs"
                  data-testid="input-new-email"
                />
                <Button onClick={addEmail} disabled={emailSaving} size="sm" data-testid="add-email-btn">
                  {emailSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
                  Agregar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Estos correos recibirán: alertas de facturas por vencer (7 días SII), solicitudes de rechazo y notificaciones del sistema.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Parte Diario - Config RRHH */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Mail className="w-5 h-5" />Configuración RRHH / Parte Diario</CardTitle>
            <CardDescription>Email de almuerzos y horario estándar de trabajo</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 items-end">
            <div className="flex-1 max-w-sm space-y-1.5">
              <Label className="text-xs">Email destino almuerzos</Label>
              <Input
                type="email"
                value={emailAlmuerzo}
                onChange={(e) => setEmailAlmuerzo(e.target.value)}
                placeholder="cocina@empresa.cl"
                className="rounded-xl"
                data-testid="input-email-almuerzo"
              />
            </div>
          </div>

          <div className="border-t pt-3">
            <Label className="text-sm font-semibold mb-2 block">Horario estándar Lunes a Jueves</Label>
            <div className="grid grid-cols-4 gap-2 max-w-md">
              <div className="space-y-1"><Label className="text-[10px]">Entrada AM</Label>
                <Input type="time" value={horario.horario_lv_entrada} onChange={e => setHorario(h => ({...h, horario_lv_entrada: e.target.value}))} className="rounded-xl text-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px]">Salida AM</Label>
                <Input type="time" value={horario.horario_lv_salida_am} onChange={e => setHorario(h => ({...h, horario_lv_salida_am: e.target.value}))} className="rounded-xl text-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px]">Entrada PM</Label>
                <Input type="time" value={horario.horario_lv_entrada_pm} onChange={e => setHorario(h => ({...h, horario_lv_entrada_pm: e.target.value}))} className="rounded-xl text-sm" /></div>
              <div className="space-y-1"><Label className="text-[10px]">Salida PM</Label>
                <Input type="time" value={horario.horario_lv_salida} onChange={e => setHorario(h => ({...h, horario_lv_salida: e.target.value}))} className="rounded-xl text-sm" /></div>
            </div>
          </div>

          <div className="border-t pt-3">
            <Label className="text-sm font-semibold mb-2 block">Horario Viernes (salida diferente)</Label>
            <div className="max-w-[120px] space-y-1">
              <Label className="text-[10px]">Salida PM Viernes</Label>
              <Input type="time" value={horario.horario_vi_salida} onChange={e => setHorario(h => ({...h, horario_vi_salida: e.target.value}))} className="rounded-xl text-sm" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Los viernes se usa esta hora en vez de la salida estándar</p>
          </div>

          <Button onClick={saveParteConfig} disabled={emailAlmuerzoSaving} className="rounded-xl gap-1.5" data-testid="btn-save-email-almuerzo">
            {emailAlmuerzoSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar Configuración RRHH
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading">Información del Sistema</CardTitle><CardDescription>Detalles de la configuración actual</CardDescription></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Versión</p><p className="font-mono text-lg font-bold">1.0.0</p></div>
            <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Ambiente</p><p className="font-mono text-lg font-bold">Producción</p></div>
            <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Base de Datos</p><p className="font-mono text-lg font-bold">MongoDB</p></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
